import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { updateActiveTask } from '../util/Util';
import { ItemTableGridApi } from '../util/itemTableFlush';

type TaskWithItems<T> = { id: string; items?: readonly T[] };

const asItemList = <T,>(items: readonly T[] | Record<string, T> | undefined): T[] => {
	if (!items) return [];
	return Array.isArray(items) ? [...items] : Object.values(items);
};

const fingerprint = <T extends { uniqueId: string; quantity?: number; price?: number; item?: string }>(
	items: readonly T[] | Record<string, T> | undefined,
) =>
	asItemList(items)
		.map((item) => [
			item.uniqueId,
			item.quantity ?? 0,
			item.price ?? 0,
			item.item ?? '',
		].join('\u0001'))
		.join('\u0000');

export const useBufferedTaskItems = <T extends { uniqueId: string }>({
	activeTask,
	setPendingTasks,
	apiRef,
	flushDelayMs = 300,
}: {
	activeTask: TaskWithItems<T> | undefined;
	setPendingTasks: Dispatch<SetStateAction<any[]>>;
	apiRef: { current: ItemTableGridApi<T> | null };
	flushDelayMs?: number;
}) => {
	const [gridRows, setGridRows] = useState<T[]>([]);
	const taskIdRef = useRef<string | undefined>(undefined);
	const flushedFingerprintRef = useRef('');
	const flushTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const gridRowsRef = useRef<T[]>([]);
	gridRowsRef.current = gridRows;

	const flushRows = useCallback((rows: readonly T[]) => {
		const rowsCopy = [...rows];
		flushedFingerprintRef.current = fingerprint(rowsCopy);
		updateActiveTask(setPendingTasks, { items: rowsCopy });
	}, [setPendingTasks]);

	const scheduleFlush = useCallback((rows: readonly T[]) => {
		if (flushTimeoutRef.current) clearTimeout(flushTimeoutRef.current);
		flushTimeoutRef.current = setTimeout(() => {
			flushTimeoutRef.current = undefined;
			flushRows(rows);
		}, flushDelayMs);
	}, [flushRows, flushDelayMs]);

	const replaceGridRows = useCallback((rows: T[]) => {
		gridRowsRef.current = rows;
		setGridRows(rows);
		scheduleFlush(rows);
	}, [scheduleFlush]);

	const flushNow = useCallback(() => {
		if (flushTimeoutRef.current) {
			clearTimeout(flushTimeoutRef.current);
			flushTimeoutRef.current = undefined;
		}
		const rows = gridRowsRef.current;
		flushRows(rows);
		return rows;
	}, [flushRows]);

	useEffect(() => {
		const attach = () => {
			if (apiRef.current) {
				apiRef.current.flushTaskItems = flushNow;
			}
		};
		attach();
		return () => {
			if (apiRef.current) {
				delete apiRef.current.flushTaskItems;
			}
		};
	}, [apiRef, flushNow]);

	useEffect(() => {
		if (!activeTask?.id) {
			taskIdRef.current = undefined;
			flushedFingerprintRef.current = '';
			gridRowsRef.current = [];
			setGridRows([]);
			return;
		}

		const idChanged = activeTask.id !== taskIdRef.current;
		if (idChanged) {
			taskIdRef.current = activeTask.id;
			if (flushTimeoutRef.current) {
				clearTimeout(flushTimeoutRef.current);
				flushTimeoutRef.current = undefined;
			}
		}

		const fp = fingerprint(activeTask.items);
		if (!idChanged && fp === flushedFingerprintRef.current) return;

		const items = asItemList(activeTask.items) as T[];
		gridRowsRef.current = items;
		setGridRows(items);
		flushedFingerprintRef.current = fp;
	}, [activeTask?.id, activeTask?.items]);

	useEffect(() => () => {
		if (flushTimeoutRef.current) {
			clearTimeout(flushTimeoutRef.current);
			flushRows(gridRowsRef.current);
		}
	}, [flushRows]);

	return {
		gridRows,
		gridRowsRef,
		replaceGridRows,
		flushNow,
	};
};